import { AddToCartComponent } from '@theme/product-form';
import { fetchConfig } from '@theme/utilities';
import { CartAddEvent } from '@theme/events';

/** @type {WeakMap<HTMLElement, Promise<string>>} */
const variantIdCache = new WeakMap();

/** @type {WeakSet<HTMLElement>} */
const observedPages = new WeakSet();

/** @type {Map<string, Promise<{ id: number, variants: Array<{ id: number, available: boolean, price: number }> }>>} */
const productCache = new Map();

/** @type {WeakSet<Element>} */
const metaSections = new WeakSet();

let recommendationsRequested = false;

/**
 * Wishlist page is injected by Vitals after the module loads.
 * Add-to-cart uses the theme button. Removal uses the same close control as the card X,
 * because Vitals only marks the row deleted and leaves the card and header count in place.
 */
function initWishlistAddToCart() {
  document.addEventListener('click', onDocumentClick, true);
  watchPages();
  new MutationObserver(watchPages).observe(document.body, { childList: true, subtree: true });
}

function isStoreWishlist(page) {
  if (page.closest('.vtl-proxy-page-share-wishlist')) return false;
  if (page.closest('.vtl-proxy-page-wishlist')) return true;
  return window.location.pathname.includes('/wishlist');
}

function watchPages() {
  document.querySelectorAll('.Vtl-WishlistPage').forEach((page) => {
    if (page instanceof HTMLElement && isStoreWishlist(page)) bindPage(page);
  });
}

/**
 * @param {HTMLElement} page
 */
function bindPage(page) {
  if (observedPages.has(page)) return;
  observedPages.add(page);

  const sync = () => {
    mountBreadcrumbs(page);
    mountToolbar(page);
    syncTitleCount(page);
    enhanceAll(page);
    finishDeletedProducts(page);
    mountRecommendations(page);
  };

  sync();
  new MutationObserver(sync).observe(page, { childList: true, subtree: true });
}

/**
 * @returns {HTMLElement | null}
 */
function chromeData() {
  const data = document.getElementById('homi-wishlist-chrome-data');
  return data instanceof HTMLElement ? data : null;
}

/**
 * @param {string} name
 * @returns {SVGElement | null}
 */
function cloneIcon(name) {
  const template = document.getElementById(`homi-wishlist-icon-${name}`);
  if (!(template instanceof HTMLTemplateElement)) return null;
  const svg = template.content.querySelector('svg');
  if (!(svg instanceof SVGElement)) return null;
  return /** @type {SVGElement} */ (svg.cloneNode(true));
}

/**
 * @param {HTMLElement} page
 */
function mountBreadcrumbs(page) {
  if (page.querySelector('.breadcrumbs')) return;

  const chrome = document.getElementById('homi-wishlist-chrome');
  const nav = chrome?.querySelector('.breadcrumbs');
  if (nav) {
    page.prepend(nav);
    return;
  }

  const data = chromeData();
  if (!data) return;

  const homeLabel = data.dataset.homeLabel || 'Home';
  const currentLabel = data.dataset.currentLabel || 'Wishlist';
  const homeUrl = data.dataset.homeUrl || '/';

  const breadcrumbs = document.createElement('nav');
  breadcrumbs.className = 'breadcrumbs';
  breadcrumbs.setAttribute('aria-label', data.dataset.breadcrumbsLabel || 'Breadcrumbs');

  const list = document.createElement('ol');
  list.className = 'breadcrumbs__list';

  const homeItem = document.createElement('li');
  homeItem.className = 'breadcrumbs__item';
  const homeLink = document.createElement('a');
  homeLink.className = 'breadcrumbs__link';
  homeLink.href = homeUrl;
  homeLink.textContent = homeLabel;
  homeItem.append(homeLink);

  const currentItem = document.createElement('li');
  currentItem.className = 'breadcrumbs__item';
  const current = document.createElement('span');
  current.className = 'breadcrumbs__current';
  current.setAttribute('aria-current', 'page');
  current.textContent = currentLabel;
  currentItem.append(current);

  list.append(homeItem, currentItem);
  breadcrumbs.append(list);
  page.prepend(breadcrumbs);
}

/**
 * @param {HTMLElement} page
 */
function syncTitleCount(page) {
  const title = page.querySelector('.Vtl-WishlistPage__Title');
  if (!(title instanceof HTMLElement)) return;

  const count = page.querySelectorAll('.Vtl-WishlistProduct').length;
  let badge = title.querySelector('[data-homi-wl-count]');
  if (!(badge instanceof HTMLElement)) {
    badge = document.createElement('span');
    badge.dataset.homiWlCount = 'true';
    badge.className = 'homi-wishlist-count';
    title.append(badge);
  }

  // Page observer watches childList: textContent always replaces the text node, so write only on change.
  const text = count > 0 ? ` (${count})` : '';
  if (badge.textContent !== text) badge.textContent = text;
}

/**
 * @param {string} text
 * @returns {HTMLSpanElement}
 */
function toolLabel(text) {
  const label = document.createElement('span');
  label.className = 'homi-wishlist-tool__label';
  label.textContent = text;
  return label;
}

/**
 * @param {HTMLElement} page
 * @returns {HTMLElement | null}
 */
function vitalsCopyButton(page) {
  const button = page.querySelector('.Vtl-WishlistPage__CopyActionWrap .Vtl-WishlistPage__ShareAction');
  return button instanceof HTMLElement ? button : null;
}

/**
 * @param {HTMLElement} page
 * @returns {HTMLElement | null}
 */
function vitalsEmailButton(page) {
  const button = page.querySelector('.Vtl-WishlistPage__ShareToolbar > .Vtl-WishlistPage__ShareAction');
  return button instanceof HTMLElement ? button : null;
}

/**
 * Vitals builds the share link only inside its copy handler, which calls clipboard.writeText synchronously.
 * @param {HTMLElement} page
 * @returns {string}
 */
function readShareUrl(page) {
  const button = vitalsCopyButton(page);
  const clipboard = navigator.clipboard;
  if (!button || !clipboard) return '';

  const { writeText } = clipboard;
  let url = '';
  clipboard.writeText = async (text) => {
    url = text;
  };
  try {
    button.click();
  } finally {
    clipboard.writeText = writeText;
  }
  return url;
}

/**
 * @param {HTMLElement} status
 */
function showCopiedStatus(status) {
  const label = chromeData()?.dataset.copiedLabel || '';
  if (status.textContent !== label) status.textContent = label;
  window.clearTimeout(Number(status.dataset.timer));
  status.dataset.timer = String(
    window.setTimeout(() => {
      status.textContent = '';
    }, 2000)
  );
}

/**
 * @param {HTMLElement} page
 * @param {HTMLElement} status
 */
async function shareWishlist(page, status) {
  const url = readShareUrl(page);
  if (!url) return;

  if (typeof navigator.share === 'function') {
    try {
      await navigator.share({ title: chromeData()?.dataset.currentLabel || document.title, url });
      return;
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
    }
  }

  await navigator.clipboard.writeText(url);
  showCopiedStatus(status);
}

/**
 * @param {string} icon
 * @param {string} label
 * @param {() => void} onClick
 * @returns {HTMLButtonElement}
 */
function createTool(icon, label, onClick) {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'homi-wishlist-tool';

  const iconWrap = document.createElement('span');
  iconWrap.className = 'homi-wishlist-tool__icon';
  const svg = cloneIcon(icon);
  if (svg) iconWrap.append(svg);

  button.append(iconWrap, toolLabel(label));
  button.addEventListener('click', onClick);
  return button;
}

/**
 * @param {HTMLElement} page
 */
function clearWishlist(page) {
  const buttons = [...page.querySelectorAll('.Vtl-WishlistProduct__CloseButton')];
  buttons.forEach((button) => {
    if (button instanceof HTMLElement) button.click();
  });
}

/**
 * Vitals renders its toolbar with Preact: moving its nodes wipes the whole page on the next render.
 * The theme toolbar sits next to it and forwards clicks to the hidden Vitals buttons.
 * @param {HTMLElement} page
 */
function mountToolbar(page) {
  const vitalsToolbar = page.querySelector('.Vtl-WishlistPage__ShareToolbar');
  const current = page.querySelector('.homi-wishlist-toolbar');

  if (!vitalsToolbar) {
    current?.remove();
    return;
  }
  if (current) return;

  const data = chromeData();
  const toolbar = document.createElement('div');
  toolbar.className = 'homi-wishlist-toolbar';

  const status = document.createElement('span');
  status.className = 'homi-wishlist-toolbar__status';
  status.setAttribute('role', 'status');

  const group = document.createElement('div');
  group.className = 'homi-wishlist-toolbar__group';
  group.append(
    createTool('share', data?.dataset.shareLabel || 'Share', () => void shareWishlist(page, status)),
    createTool('email', data?.dataset.emailLabel || 'Email', () => vitalsEmailButton(page)?.click()),
    createTool('link', data?.dataset.copyLabel || 'Copy link', () => {
      const button = vitalsCopyButton(page);
      if (!button) return;
      button.click();
      showCopiedStatus(status);
    })
  );

  const divider = document.createElement('span');
  divider.className = 'homi-wishlist-toolbar__divider';
  divider.setAttribute('aria-hidden', 'true');

  const clearButton = document.createElement('button');
  clearButton.type = 'button';
  clearButton.className = 'homi-wishlist-clear';
  const trash = cloneIcon('trash');
  if (trash) clearButton.append(trash);
  clearButton.append(document.createTextNode(data?.dataset.clearLabel || 'Clear all'));
  clearButton.addEventListener('click', () => clearWishlist(page));

  toolbar.append(group, divider, clearButton, status);
  vitalsToolbar.after(toolbar);
}

/**
 * @param {Event} event
 */
function onDocumentClick(event) {
  if (!(event.target instanceof Element) || !(event instanceof MouseEvent)) return;

  const button = event.target.closest('.Vtl-WishlistProduct__AddToCart');
  if (!(button instanceof HTMLButtonElement)) return;

  const productEl = button.closest('.Vtl-WishlistProduct');
  if (!(productEl instanceof HTMLElement)) return;

  if (button.dataset.homiAtcReady !== 'true') {
    enhanceProduct(productEl);
  }

  if (button.dataset.homiAtcReady !== 'true') return;

  event.preventDefault();
  event.stopPropagation();
  event.stopImmediatePropagation();

  const component = button.closest('add-to-cart-component');
  if (!(component instanceof AddToCartComponent)) return;

  void handleAdd(productEl, button, component, event);
}

/**
 * @param {Element} root
 */
function enhanceAll(root) {
  root.querySelectorAll('.Vtl-WishlistProduct').forEach((productEl) => {
    if (productEl instanceof HTMLElement) {
      enhanceProduct(productEl);
      mountProductMeta(productEl);
    }
  });
}

/**
 * @param {HTMLElement} productEl
 */
function enhanceProduct(productEl) {
  const button = productEl.querySelector('.Vtl-WishlistProduct__AddToCart');
  if (!(button instanceof HTMLButtonElement) || button.dataset.homiAtcReady === 'true') return;

  const template = document.getElementById('homi-wishlist-atc-button-content');
  if (!(template instanceof HTMLTemplateElement) || !button.parentNode) return;

  button.type = 'button';
  button.classList.add('button', 'quick-add__button', 'add-to-cart-button');
  button.innerHTML = template.innerHTML;
  button.style.removeProperty('background-color');
  button.style.removeProperty('color');
  button.setAttribute('ref', 'addToCartButton');

  const component = document.createElement('add-to-cart-component');
  component.dataset.addToCartAnimation = template.dataset.addToCartAnimation === 'true' ? 'true' : 'false';

  const image = productEl.querySelector('.Vtl-WishlistProduct__Image');
  if (image instanceof HTMLImageElement && image.src) {
    component.dataset.productVariantMedia = image.src;
  }

  const form = document.createElement('form');
  form.className = 'homi-wishlist-atc-form';
  form.noValidate = true;
  form.addEventListener('submit', (submitEvent) => submitEvent.preventDefault());

  const parent = button.parentNode;
  const next = button.nextSibling;
  component.appendChild(button);
  form.appendChild(component);
  parent.insertBefore(form, next);
  button.dataset.homiAtcReady = 'true';
}

/**
 * Vitals leaves a card in the "added" state after a server delete. Finish removal the same way as the X control.
 * @param {ParentNode} root
 */
function finishDeletedProducts(root) {
  root.querySelectorAll('.Vtl-WishlistProduct').forEach((productEl) => {
    if (!(productEl instanceof HTMLElement) || productEl.dataset.homiWlRemoving === 'true') return;
    if (productEl.querySelector('.Vtl-WishlistProduct__AddToCart')) return;
    if (!productEl.querySelector('.Vtl-WishlistProduct__AddedToCart')) return;
    removeFromWishlist(productEl);
  });
}

/**
 * @param {HTMLElement} productEl
 */
function removeFromWishlist(productEl) {
  if (productEl.dataset.homiWlRemoving === 'true') return;

  const closeButton = productEl.querySelector('.Vtl-WishlistProduct__CloseButton');
  if (!(closeButton instanceof HTMLElement)) return;

  productEl.dataset.homiWlRemoving = 'true';
  closeButton.click();
}

/**
 * @param {HTMLElement} productEl
 * @param {HTMLButtonElement} button
 * @param {AddToCartComponent} component
 * @param {MouseEvent} event
 */
async function handleAdd(productEl, button, component, event) {
  if (button.disabled || productEl.dataset.homiWlRemoving === 'true') return;

  button.disabled = true;
  component.handleClick(event);

  try {
    const variantId = await resolveVariantId(productEl, button);
    const response = await addVariantToCart(variantId);

    if (response.status) {
      throw new Error(response.message || 'Cart add failed');
    }

    const cart = await fetch('/cart.js').then((res) => res.json());

    document.documentElement.dispatchEvent(
      new CartAddEvent(cart, variantId, {
        source: 'product-form-component',
        itemCount: 1,
        variantId,
      })
    );

    removeFromWishlist(productEl);
  } catch (error) {
    button.disabled = false;
    button.removeAttribute('data-added');
    console.error(error);
  }
}

/**
 * @param {HTMLElement} productEl
 * @param {HTMLButtonElement} button
 * @returns {Promise<string>}
 */
async function resolveVariantId(productEl, button) {
  const cached = variantIdCache.get(productEl);
  if (cached) return cached;

  const promise = resolveVariantIdInternal(productEl, button);
  variantIdCache.set(productEl, promise);
  return promise;
}

/**
 * @param {HTMLElement} productEl
 * @param {HTMLButtonElement} button
 * @returns {Promise<string>}
 */
async function resolveVariantIdInternal(productEl, button) {
  const fromButton =
    button.getAttribute('data-variant-id') ||
    button.dataset.variantId ||
    button.getAttribute('data-id') ||
    productEl.getAttribute('data-variant-id') ||
    productEl.dataset.variantId;

  if (fromButton) return String(fromButton);

  const url = productUrl(productEl);
  if (!url) {
    throw new Error('Wishlist product link not found');
  }

  const fromQuery = url.searchParams.get('variant');
  if (fromQuery) return fromQuery;

  const product = await fetchProduct(url);
  const variant = product.variants?.find((/** @type {{ available: boolean }} */ v) => v.available) ?? product.variants?.[0];

  if (!variant?.id) {
    throw new Error('Wishlist variant not found');
  }

  return String(variant.id);
}

/**
 * @param {HTMLElement} productEl
 * @returns {URL | null}
 */
function productUrl(productEl) {
  const link = productEl.querySelector('a[href*="/products/"]');
  return link instanceof HTMLAnchorElement ? new URL(link.href, window.location.origin) : null;
}

/**
 * @param {URL} url
 */
function fetchProduct(url) {
  const path = url.pathname.replace(/\/$/, '');
  let promise = productCache.get(path);
  if (!promise) {
    promise = fetch(`${path}.js`).then((response) => {
      if (!response.ok) throw new Error('Wishlist product data not found');
      return response.json();
    });
    promise.catch(() => productCache.delete(path));
    productCache.set(path, promise);
  }
  return promise;
}

/**
 * Appends stock and BGN price to the Vitals price row. Append only: see mountToolbar.
 * @param {HTMLElement} productEl
 */
function mountProductMeta(productEl) {
  const priceSection = productEl.querySelector('.Vtl-WishlistProduct__PriceSection');
  const url = productUrl(productEl);
  if (!priceSection || !url || metaSections.has(priceSection)) return;
  metaSections.add(priceSection);

  fetchProduct(url)
    .then((product) => {
      const variantId = Number(url.searchParams.get('variant'));
      const variant = product.variants.find((item) => item.id === variantId) ?? product.variants[0];
      if (!variant || !priceSection.isConnected) return;

      const data = chromeData();
      const stock = document.createElement('span');
      stock.className = variant.available ? 'homi-wishlist-stock' : 'homi-wishlist-stock homi-wishlist-stock--out';
      stock.textContent = (variant.available ? data?.dataset.inStockLabel : data?.dataset.outOfStockLabel) || '';
      priceSection.append(stock);

      const rate = Number(data?.dataset.bgnRate);
      if (rate > 0) {
        const bgn = document.createElement('span');
        bgn.className = 'homi-wishlist-bgn';
        bgn.textContent = `${Math.round(variant.price * rate) / 100} ${data?.dataset.bgnSuffix || ''}`.trim();
        priceSection.append(bgn);
      }
    })
    .catch(() => {});
}

/**
 * Loads the wishlist-group recommendations for the first wishlist product.
 * The wrapper stays hidden until real cards arrive: without a product the section renders placeholders.
 * @param {HTMLElement} page
 */
function mountRecommendations(page) {
  if (recommendationsRequested) return;

  const wrapper = document.querySelector('[data-homi-wishlist-recommendations]');
  const element = wrapper?.querySelector('product-recommendations');
  const productEl = page.querySelector('.Vtl-WishlistProduct');
  const url = productEl instanceof HTMLElement ? productUrl(productEl) : null;
  if (!(wrapper instanceof HTMLElement) || !(element instanceof HTMLElement) || !url) return;

  recommendationsRequested = true;

  const reveal = new MutationObserver(() => {
    if (!wrapper.querySelector('.product-recommendations__heading')) return;
    reveal.disconnect();
    wrapper.hidden = false;
  });
  reveal.observe(wrapper, { childList: true, subtree: true });

  fetchProduct(url)
    .then((product) => {
      element.dataset.productId = String(product.id);
    })
    .catch(() => reveal.disconnect());
}

/**
 * @param {string} variantId
 * @returns {Promise<Record<string, unknown>>}
 */
async function addVariantToCart(variantId) {
  const formData = new FormData();
  formData.set('id', variantId);
  formData.set('quantity', '1');

  const fetchCfg = fetchConfig('javascript', { body: formData });

  const response = await fetch(Theme.routes.cart_add_url, {
    ...fetchCfg,
    headers: {
      ...fetchCfg.headers,
      Accept: 'text/html',
    },
  });

  return response.json();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initWishlistAddToCart);
} else {
  initWishlistAddToCart();
}
